import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Slider,
  Snackbar,
  Typography,
} from "@mui/material";

import CachedIcon from "@mui/icons-material/Cached";
import InfoIcon from "@mui/icons-material/Info";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";

import React, { useEffect, useState } from "react";
import CustomCard from "../molecules/ChallengeCreator/CustomCard";
import { useTheme } from "styled-components";

import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FormInputField from "../atoms/Form/FormInputField";
import FormTextareaField from "../atoms/Form/FormTextareaField";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useUserProfile from "../../hooks/useUserProfile";
import FormTagInputField from "../atoms/Form/FormTagInputField";
import {
  handleCreateNewSubmissionDraft,
  saveDraft,
} from "../../utils/draftUtils";
import { initiatePurchase } from "../../services/purchaseService";
import Loading from "../atoms/Loading";
import { ErrorText } from "../atoms/Form/SharedFormStyles";
import FormSelectField from "../atoms/Form/FormSelectField";
import useDraftSubmission from "../../hooks/useDraftSubmission";
import { updateMetadataOnArtworkSubmission } from "../../utils/api";
import UploadArtworkMedia from "../molecules/UploadArtwork/UploadArtworkMedia";
import FormYearPickerField from "../atoms/Form/FormYearPickerField";
import UploadArtworkPaymentSection from "../molecules/UploadArtwork/UploadArtworkPaymentSection";
import { handleArtworkPaypalPayment } from "../../utils/submissionUtils";
import { uploadArtworkFormSchema } from "../../schemas";
import { fetchUserMembershipsFromFirestore } from "../../services/profileService";
import { getEditionConfiguration } from "../../services/submissionService";
import usePublishArtworkSubmission from "../../hooks/usePublishArtworkSubmission";

const UploadArtworkPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const submissionId = searchParams.get("submissionId");

  const theme = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [currentMediaUrl, setCurrentMediaUrl] = useState("");
  const [pageCurrentStep, setPageCurrentStep] = useState("edit");
  const [paySubmissionFee, setPaySubmissionFee] = useState(true);
  const [isPublished, setIsPublished] = useState(false);
  const [membership, setMembership] = useState({ data: [], isActive: false });
  const [minimumPricePerEdition, setMinimumPricePerEdition] = useState(4);
  const [maximumPricePerEdition, setMaximumPricePerEdition] = useState(2500);

  const [minimumNumbersOfEditions, setMinimumNumbersOfEditions] = useState(1);
  const [maximumNumbersOfEditions, setMaximumNumbersOfEditions] = useState(400);

  const [disableSaveDraftCriteria, setDisableSaveDraftCriteria] =
    useState(false);
  const [loadings, setLoadings] = useState({
    isSavingDraft: false,
    isPublishing: false,
    isPaying: false,
    isResolvingMediaUrl: false,
  });

  // Snackbar state
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // default severity
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const {
    publishSubmission,
    loading: publishLoading,
    error: publishError,
  } = usePublishArtworkSubmission();

  const {
    profileData,
    loading: profileLoading,
    error: profileError,
  } = useUserProfile(currentUser?.uid);

  const {
    draftData,
    loading: draftLoading,
    error: draftError,
    notFound: draftNotFound,
  } = useDraftSubmission(submissionId); // Real-time updates

  const submissionForm = useForm({
    resolver: zodResolver(uploadArtworkFormSchema),
    defaultValues: {
      title: "",
      firstName: "",
      lastName: "",
      isArtist: false,
      markedAsMature: false,
      allowedWallpaper: false,
      createdYear: new Date().getFullYear(),
      editionPrice: minimumPricePerEdition,
      numberOfEditions: minimumNumbersOfEditions,
      artMedium: "",
      artDescription: "",
      subjectAndStyle: [],
      websiteAddress: "",
      tags: [],
    },
  });

  const { errors } = submissionForm.formState;

  // Handle the save draft action
  const handleSaveDraft = async (
    additionalData = {},
    customSnackbarMessage = "",
    callFromButton = false,
  ) => {
    try {
      setLoadings((prev) => ({ ...prev, isSavingDraft: true }));

      // Validate the form & get the form data
      const isValid = await submissionForm.trigger("videoUploaded");
      const formData = submissionForm.getValues();

      if (!isValid && !formData.videoUploaded) {
        setSnackbarMessage("Please upload your media, to save your draft.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      // Prepare the draft payload
      const draftPayload = getLatestDraftPayload(additionalData);
      if (submissionId && submissionId !== "new") {
        // Update existing draft
        await saveDraft({
          submissionId,
          currentUser,
          draftPayload,
          setSnackbarMessage,
          customSnackbarMessage,
        });
        await updateMetadataOnArtworkSubmission(
          formData,
          draftPayload,
          true
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        // Create a new draft
        const newSubmissionId = await handleCreateNewSubmissionDraft({
          ...draftPayload,
          userId: currentUser.uid,
        });

        if (newSubmissionId) {
          setSearchParams({ submissionId: newSubmissionId });
        }
        setSnackbarMessage(
          customSnackbarMessage
            ? customSnackbarMessage
            : "New Submission Draft Saved Successfully."
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }

      if (callFromButton)
        navigate("/marketplace/creators")
      
    } catch (error) {
      console.error("Error saving draft:", error);
      setSnackbarMessage(
        error?.message
          ? error.message
          : "Failed to save draft. Please try again."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadings((prev) => ({ ...prev, isSavingDraft: false }));
    }
  };

  //Helper function to get the latest draft payload
  const getLatestDraftPayload = (additionalData = {}) => {
    const formData = submissionForm.getValues();
    const draftPayload = {
      ...draftData, // Preserve existing data
      title: formData.title,
      firstName: formData.firstName,
      lastName: formData.lastName,
      isArtist: formData.isArtist,
      markedAsMature: formData.markedAsMature,
      allowedWallpaper: formData.allowedWallpaper,
      createdYear: formData.createdYear,
      artMedium: formData.artMedium,
      description: formData.artDescription,
      editionPrice: formData.editionPrice,
      numberOfEditions: formData.numberOfEditions,
      fundingGoal: formData.editionPrice,
      tags: formData.tags,
      subjectAndStyle: formData.subjectAndStyle,
      websiteAddress: formData.websiteAddress,
      userId: currentUser.uid,
      status: "draft",
      updatedAt: new Date(),
      profileData: { ...profileData },
      ...additionalData, // Merge additional data like submissionSaleId, saleId, fileName, contentType
      videoUploaded: formData.videoUploaded,
      digitalSupply: formData.numberOfEditions,
    };

    return draftPayload;
  };

  // Handle the draft uploaded action
  const handleVideoUploaded = (isUploaded, submissionData = {}) => {
    if (isUploaded) {
      submissionForm.setValue("videoUploaded", true); // setting the videoUploaded to true in the form
      handleSaveDraft(
        { videoUploaded: true, ...submissionData },
        "Art Media Successfully Uploaded."
      );
    } else {
      setSnackbarMessage("Failed to upload the Media. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handler to update isUploadingVideo state
  const handleUploadingChange = (uploading) => {
    console.log("Uploading status changed:", uploading);
    setIsUploadingVideo(uploading);
  };

  const handleOnFileSelectionChange = (file) => {
    // console.log("File selected:", file);
    submissionForm.clearErrors("videoUploaded");
  };

  // Handle the submit challenge action
  const handlePublishArtwork = async () => {
    try {
      setLoadings((prev) => ({ ...prev, isPublishing: true }));
      // Validate the form & get the form data
      const isValid = await submissionForm.trigger();
      if (!isValid) {
        setSnackbarMessage("Please fill in all required fields.");
        setSnackbarSeverity("error");
        return;
      }
      const formData = submissionForm.getValues();

      // No validation errors, proceed with updating the submission status
      if (!submissionId) {
        setSnackbarMessage(
          "Draft ID not found. Please try saving the draft first."
        );
        setSnackbarOpen(true);
        return;
      }

      // Ensure draftData has the latest values
      if (!draftData) {
        setSnackbarMessage("Draft data is not available. Please try again.");
        setSnackbarOpen(true);
        return;
      }

      // Call the updateArtworkMetadata API function
      const updatedMetadata = await updateMetadataOnArtworkSubmission(
        formData,
        draftData,
        false
      );

      console.log("updatedMetadata: ", updatedMetadata);

      // Prepare the draft payload
      const submissionPayload = getLatestDraftPayload();

      // Proceed to publish the submission
      const success = await publishSubmission(submissionId, submissionPayload);

      if (success) {
        setIsPublished(true);
        setSnackbarMessage("Submission successfully published.");
        setSnackbarSeverity("success");
        navigate(`/challenge/profile/${currentUser.uid}`);
      } else {
        setSnackbarMessage("Failed to publish submission. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error publishing submission:", error);
      setSnackbarMessage("Failed to publish submission. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadings((prev) => ({ ...prev, isPublishing: false }));
    }
  };

  // Handle payment action
  const handlePaymentAction = async (paymentData) => {
    setLoadings((prev) => ({ ...prev, isPaying: true }));
    const draftPayload = getLatestDraftPayload();

    await handleArtworkPaypalPayment({
      currentUser,
      paymentData,
      initiatePurchase,
      profileData,
      saveDraft,
      setSnackbarMessage,
      setSnackbarOpen,
      submissionId: submissionId || draftData?.submissionId,
      draftData: draftPayload,
    });

    setLoadings((prev) => ({ ...prev, isPaying: false }));
  };

  // Handle the save draft & proceed to next step action
  const handleProceedClick = async () => {
    // Trigger form validation
    const isValid = await submissionForm.trigger();
    if (!isValid) {
      setSnackbarMessage(
        "Please fill in all required fields, before proceeding to next step."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Save the draft before proceeding to next step
    await handleSaveDraft();

    // Proceed to next step
    setPageCurrentStep("payment");
  };

  // Helper function to check if there are any active and expired memberships
  const isAnyMembershipActiveAndExpired = (memberships) => {
    const now = new Date();
    return memberships.some((membership) => {
      if (membership.status !== "active") return false;

      const paypalData = membership.paypalData || {};
      const { nextBillingTime } = paypalData;

      let compareDate;

      if (nextBillingTime) {
        compareDate = new Date(nextBillingTime);
      } else if (membership.updatedAt?.seconds) {
        compareDate = new Date(membership.updatedAt.seconds * 1000);
      } else {
        return false;
      }

      return compareDate < now;
    });
  };

  // Helper function to the media url is available
  const waitForUrlAvailable = async (url, timeout = 10000, interval = 1000) => {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      try {
        const res = await fetch(url, { method: "HEAD", cache: "no-store" });
        if (res.ok) return true;
      } catch (_) {
        // Fail silently, as 404 is expected until upload finishes
      }

      await new Promise((resolve) => setTimeout(resolve, interval));
    }

    return false;
  };

  const SUBJECT_OPTIONS = [
    "Nature",
    "Asian art",
    "People",
    "Abstract",
    "Animals",
    "Urban",
    "Gourmet",
    "Narrative",
  ];

  const ART_MEDIUM_OPTIONS = [
    { value: "painting", label: "Painting" },
    { value: "sculpture", label: "Sculpture" },
    { value: "digital", label: "Digital Art" },
    { value: "photography", label: "Photography" },
  ];

  // Handle payment completion
  useEffect(() => {
    if (draftData && draftData.status === "published") {
      setIsPublished(true);
    }

    if (draftData && draftData.paymentData.status !== "INCOMPLETE") {
      console.log("Current user has paid one time fee for the submission. ");
      setPaySubmissionFee(false);
    }

    if (
      (profileData && profileData.subscriptionStatus !== "") ||
      (membership.data.length > 0 &&
        isAnyMembershipActiveAndExpired(membership.data))
    ) {
      console.log("The current user is an active cap member.");
      setMembership((prev) => ({ ...prev, isActive: true }));
      setPaySubmissionFee(false);
    }
  }, [draftData, profileData, membership.data]);

  //Fetch Current User's Membership Status
  useEffect(() => {
    const handleFetchCurrentUserMemberships = async (userUid) => {
      try {
        const fetchedMemberships = await fetchUserMembershipsFromFirestore(
          userUid
        );

        setMembership((prev) => ({ ...prev, data: fetchedMemberships }));
      } catch (error) {
        setSnackbarMessage("Failed to fetch user memberships.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };

    if (currentUser.uid) {
      handleFetchCurrentUserMemberships(currentUser.uid);
    }
  }, [currentUser.uid]);

  useEffect(() => {
    if (draftNotFound) {
      // setSnackbarMessage("Draft not found. Please try saving the draft first.");
      // setSnackbarSeverity("warning");
      // setSnackbarOpen(true);

      console.log("Draft not found. Please try saving the draft first.");
    }
  }, [draftNotFound]);

  // Change the first name when the user is an artist
  useEffect(() => {
    if (submissionForm.watch("isArtist")) {
      if (profileData?.displayName) {
        const [firstName, ...lastNameParts] =
          profileData.displayName.split(" ");
        submissionForm.setValue("firstName", firstName || "");
        submissionForm.setValue("lastName", lastNameParts.join(" ") || "");
      }
    }
    // Intentionally keyed on the isArtist toggle alone. submissionForm is a
    // stable RHF instance, and re-running this when profileData changes would
    // overwrite a name the user had already typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionForm.watch("isArtist")]);

  //get Edition Configuration from web admin (set min max value for price and edition)
  useEffect(() => {
    const getConfiguration = async () => {
      const response = await getEditionConfiguration();
      if (response) {
        setMinimumPricePerEdition(parseFloat(response?.minimumPricePerEdition));
        setMaximumPricePerEdition(parseFloat(response?.maximumPricePerEdition));

        setMinimumNumbersOfEditions(
          parseFloat(response?.minimumNumbersOfEditions)
        );
        setMaximumNumbersOfEditions(
          parseFloat(response?.maximumNumbersOfEditions)
        );
      }
    };
    getConfiguration();
  }, []);
  // Setting the draft data to the form
  useEffect(() => {
    console.log("draftData: ", draftData);

    const constructedMediaUrl = async () => {
      if (!draftData?.contentType || !draftData?.submissionSaleId) return;

      // Construct base URL depending on content type
      const baseUrl = draftData.contentType.startsWith("video/")
        ? `https://swayer.blob.core.windows.net/${draftData.submissionSaleId}-pub/thumbnail_master.png`
        : draftData.contentType.startsWith("image/")
        ? `https://swayer.blob.core.windows.net/${draftData.submissionSaleId}-pub/_thumb_large.jpg`
        : "";

      if (!baseUrl) return;

      // Add cache-busting param
      const urlWithCacheBusting = `${baseUrl}?cb=${Date.now()}`;

      try {
        setLoadings((prev) => ({ ...prev, isResolvingMediaUrl: true }));

        const isAvailable = await waitForUrlAvailable(
          urlWithCacheBusting,
          15000,
          1000
        );

        if (isAvailable) {
          console.log("✅ Media URL is available:", urlWithCacheBusting);
          setCurrentMediaUrl(urlWithCacheBusting);
        } else {
          console.warn("⏱ Media URL not available after timeout:", baseUrl);
        }
      } catch (error) {
        console.error("❌ Error while resolving media URL:", error);
      } finally {
        setLoadings((prev) => ({ ...prev, isResolvingMediaUrl: false }));
      }
    };

    if (
      draftData?.submissionSaleId &&
      draftData?.videoUploaded &&
      draftData?.contentType
    ) {
      constructedMediaUrl();
    }

    if (draftData) {
      submissionForm.setValue("title", draftData?.title || "");
      submissionForm.setValue("firstName", draftData?.firstName || "");
      submissionForm.setValue("lastName", draftData?.lastName || "");
      submissionForm.setValue("isArtist", draftData?.isArtist || false);
      submissionForm.setValue(
        "markedAsMature",
        draftData?.markedAsMature || false
      );
      submissionForm.setValue(
        "allowedWallpaper",
        draftData?.allowedWallpaper || false
      );
      submissionForm.setValue(
        "createdYear",
        draftData?.createdYear || new Date().getFullYear()
      );
      submissionForm.setValue(
        "editionPrice",
        draftData?.editionPrice || minimumPricePerEdition
      );
      submissionForm.setValue(
        "numberOfEditions",
        draftData?.numberOfEditions || minimumNumbersOfEditions
      );
      submissionForm.setValue("artMedium", draftData?.artMedium || "");
      submissionForm.setValue("artDescription", draftData?.description || "");
      submissionForm.setValue(
        "subjectAndStyle",
        draftData?.subjectAndStyle || []
      );
      submissionForm.setValue(
        "websiteAddress",
        draftData?.websiteAddress || ""
      );
      submissionForm.setValue("tags", draftData?.tags || []);
      submissionForm.setValue(
        "videoUploaded",
        draftData?.videoUploaded || false
      );
    }
    // Hydrates the form from the loaded draft, so it must fire on draftData
    // only. submissionForm is stable, and re-running when the edition-config
    // minimums arrive would reset fields the user is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftData]);

  // setting the submission id when no submission id is provided
  useEffect(() => {
    if (!submissionId || submissionId === "undefined") {
      setSearchParams({
        submissionId: "new",
      });
    }
  }, [setSearchParams, submissionId]);

  //Removing Form Validation Errors On Required Fields Change
  // Pulled out of the dependency array so it holds plain values the lint rule
  // can check statically. These are the same watch() calls as before, just
  // named, so the effect still re-runs on exactly the same changes.
  const watchedVideoUploaded = submissionForm.watch("videoUploaded");
  const watchedTitle = submissionForm.watch("title");
  const watchedLastName = submissionForm.watch("lastName");
  const watchedCreatedYear = submissionForm.watch("createdYear");
  const watchedArtMedium = submissionForm.watch("artMedium");
  const watchedSubjectAndStyle = submissionForm.watch("subjectAndStyle");
  const watchedWebsiteAddress = submissionForm.watch("websiteAddress");
  const watchedEditionPrice = submissionForm.watch("editionPrice");

  useEffect(() => {
    if (watchedVideoUploaded) submissionForm.clearErrors("videoUploaded");
    if (watchedTitle) submissionForm.clearErrors("title");
    if (watchedLastName) submissionForm.clearErrors("lastName");
    if (watchedCreatedYear) submissionForm.clearErrors("createdYear");
    if (watchedArtMedium) submissionForm.clearErrors("artMedium");
    if (watchedSubjectAndStyle?.length > 0)
      submissionForm.clearErrors("subjectAndStyle");
    if (watchedWebsiteAddress) submissionForm.clearErrors("websiteAddress");
    if (watchedEditionPrice) submissionForm.clearErrors("editionPrice");
    // submissionForm is a stable RHF instance, so it is not a meaningful dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedVideoUploaded,
    watchedTitle,
    watchedLastName,
    watchedCreatedYear,
    watchedArtMedium,
    watchedSubjectAndStyle,
    watchedWebsiteAddress,
    watchedEditionPrice,
  ]);

  // Argless watch() returns a fresh object on every render, so using it as a
  // dependency re-ran the comparison below on every keystroke and every
  // unrelated re-render. useWatch holds the values in state, so its identity
  // changes only when a field actually changes.
  const watchedFormValues = useWatch({ control: submissionForm.control });

  // Checking if the form data is the same as the draft data and setting the disable save draft criteria
  useEffect(() => {
    if (watchedFormValues && draftData) {
      const {
        profileData: newProfileData,
        updatedAt: newUpdatedAt,
        ...restFormData
      } = getLatestDraftPayload();
      const {
        profileData: oldProfileData,
        updatedAt: oldUpdatedAt,
        ...restDraftData
      } = draftData;

      // console.log(
      //   "Is formData === draftData? ",
      //   JSON.stringify(restFormData) === JSON.stringify(restDraftData)
      // );

      if (JSON.stringify(restFormData) === JSON.stringify(restDraftData)) {
        setDisableSaveDraftCriteria(true);
      } else {
        setDisableSaveDraftCriteria(false);
      }
    }
    // getLatestDraftPayload reads current values via the form instance at call
    // time, so it does not need to be a dependency; including it would restore
    // the every-render behaviour this effect was rewritten to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFormValues, draftData]);

  // Loading state: show a loading indicator if any of the data is loading
  if (profileLoading || draftLoading) {
    return <Loading isLoading={profileLoading || draftLoading} />;
  }

  // Error state: show an error message if any of the errors occur
  const combinedError = profileError || draftError || publishError;
  if (combinedError) {
    return (
      <Snackbar
        open
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={
          combinedError.message || "An error occurred. Please try again."
        }
      />
    );
  }

  return (
    <Container maxWidth="md">
      <Backdrop
        sx={{
          color: theme.colors.white || "white",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        open={publishLoading} // Control visibility based on publishLoading
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {pageCurrentStep === "edit" && isPublished && (
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.colors.green}, ${theme.colors.amber}, ${theme.colors.paleBlue}) `,
            margin: "4rem 0",
            padding: "1rem 2rem",
            color: "white",
            fontWeight: "bold",
            fontSize: "2rem",
            borderRadius: "0 1.5rem 1.5rem 1.5rem",
            textAlign: "center",
            position: "relative",
          }}
        >
          Your Artwork has been successfully published!
          <InfoIcon
            sx={{
              position: "absolute",
              left: "-1rem",
              top: "-1rem",
              fontSize: "3rem",
              color: theme.colors.paleBlue,
              background: "white",
              borderRadius: "50%",
            }}
          />
        </Box>
      )}

      {/* Upload Artwork Form Section */}
      {pageCurrentStep === "edit" && (
        <Box component="form">
          {/* Step - 1: Upload your art section */}
          <CustomCard
            title={"Step 1: Upload your art"}
            sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {draftData?.videoUploaded ||
            submissionForm.getValues("videoUploaded") ? (
              <div
                style={{
                  color: theme.colors.white,
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  alignItems: "center",
                }}
              >
                {currentMediaUrl && !loadings.isResolvingMediaUrl && (
                  <img
                    src={currentMediaUrl}
                    alt="Thumbnail"
                    loading="lazy"
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "1rem",
                    }}
                  />
                )}
                {loadings.isResolvingMediaUrl && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: ".5rem",
                    }}
                  >
                    <CachedIcon
                      sx={{ marginRight: "6px", fontSize: "1.2rem" }}
                      className="spin-animation"
                    />
                    <p>Waiting for media...</p>
                  </div>
                )}
                {draftData?.fileName} successfully uploaded{" "}
              </div>
            ) : (
              <UploadArtworkMedia
                onUpload={handleVideoUploaded} // Pass the correct handler
                onUploadingChange={handleUploadingChange} // Pass the new handler
                draftData={draftData}
                onFileSelectionChange={handleOnFileSelectionChange}
                required
              />
            )}

            {errors.videoUploaded?.message && (
              <ErrorText variant="body2">
                {errors.videoUploaded?.message}
              </ErrorText>
            )}
          </CustomCard>

          {/* Step - 2: Tell us about the art section */}
          <CustomCard
            title={"Step 2: Tell us about the art"}
            sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <FormInputField
              label="What would you like to title your artwork?"
              control={submissionForm.control}
              name="title"
              disabled={isPublished}
              placeholder={"Title your piece"}
              error={errors.title}
              required
            />

            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <FormInputField
                  label="First name"
                  disabled={isPublished || submissionForm.watch("isArtist")}
                  control={submissionForm.control}
                  name="firstName"
                  placeholder={"First Name"}
                  error={errors.firstName}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <FormInputField
                  label="Last name"
                  disabled={isPublished || submissionForm.watch("isArtist")}
                  control={submissionForm.control}
                  name="lastName"
                  placeholder={"Last Name"}
                  error={errors.lastName}
                  required
                />
              </Grid>

              <Grid item xs={6} lg={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...submissionForm.register("isArtist")}
                      checked={submissionForm.watch("isArtist")}
                      disabled={isPublished}
                    />
                  }
                  label="Are you an artist?"
                  sx={{ color: theme.colors.white }}
                />
              </Grid>

              <Grid item xs={6} lg={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...submissionForm.register("markedAsMature")}
                      checked={submissionForm.watch("markedAsMature")}
                      disabled={isPublished}
                    />
                  }
                  label="Mark as mature content?"
                  sx={{ color: theme.colors.white }}
                />
              </Grid>

              <Grid item xs={6} lg={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...submissionForm.register("allowedWallpaper")}
                      checked={submissionForm.watch("allowedWallpaper")}
                      disabled={isPublished}
                    />
                  }
                  label="Allow as wallpaper?"
                  sx={{ color: theme.colors.white }}
                />
              </Grid>
            </Grid>

            <FormYearPickerField
              label="Which year was the art created?"
              disabled={isPublished}
              control={submissionForm.control}
              name="createdYear"
              error={errors.createdYear}
              required
              startYear={1250}
            />
          </CustomCard>

          {/* Step - 3: Edition and Price section */}
          <CustomCard
            title={"Step 3: Edition and Price"}
            sx={{ display: "flex", flexDirection: "column", gap: "3rem" }}
          >
            {/* Edition Price slider */}
            <Box>
              <Typography gutterBottom sx={{ color: theme.colors.white }}>
                Set your price for each edition:
              </Typography>
              <Controller
                name="editionPrice"
                control={submissionForm.control}
                render={({ field }) => (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Slider
                        {...field}
                        disabled={isPublished}
                        min={minimumPricePerEdition}
                        max={maximumPricePerEdition}
                        step={1}
                        valueLabelDisplay="auto"
                        onChange={(_, value) => field.onChange(value)}
                        color="success"
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.colors.green,
                          fontWeight: "bold",
                          fontSize: "1.5rem",
                        }}
                      >
                        ${field.value}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <IconButton
                          onClick={() =>
                            submissionForm.setValue(
                              "editionPrice",
                              Math.max(minimumPricePerEdition, field.value - 1)
                            )
                          }
                          size="small"
                        >
                          <ExpandCircleDownIcon
                            style={{
                              rotate: "90deg",
                              color: theme.colors.green,
                            }}
                          />
                        </IconButton>

                        <IconButton
                          onClick={() =>
                            submissionForm.setValue(
                              "editionPrice",
                              Math.min(maximumPricePerEdition, field.value + 1)
                            )
                          }
                          size="small"
                        >
                          <ExpandCircleDownIcon
                            style={{
                              rotate: "270deg",
                              color: theme.colors.green,
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              />
              {errors.editionPrice && (
                <Typography color="error">
                  {errors.editionPrice.message}
                </Typography>
              )}
            </Box>

            {/* Number of Editions Slider*/}
            <Box>
              <Typography gutterBottom sx={{ color: theme.colors.white }}>
                Set the number of editions to issue:
              </Typography>
              <Controller
                name="numberOfEditions"
                control={submissionForm.control}
                render={({ field }) => (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Slider
                        {...field}
                        disabled={isPublished}
                        min={minimumNumbersOfEditions}
                        max={maximumNumbersOfEditions}
                        step={1}
                        valueLabelDisplay="auto"
                        sx={{ flexGrow: 1, mr: 2 }}
                        onChange={(_, value) => field.onChange(value)}
                        color="warning"
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.colors.amber,
                          fontWeight: "bold",
                          fontSize: "1.5rem",
                        }}
                      >
                        {field.value}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <IconButton
                          onClick={() =>
                            submissionForm.setValue(
                              "numberOfEditions",
                              Math.max(
                                minimumNumbersOfEditions,
                                field.value - 1
                              )
                            )
                          }
                          size="small"
                        >
                          <ExpandCircleDownIcon
                            style={{
                              rotate: "90deg",
                              color: theme.colors.amber,
                            }}
                          />
                        </IconButton>

                        <IconButton
                          onClick={() =>
                            submissionForm.setValue(
                              "numberOfEditions",
                              Math.min(
                                maximumNumbersOfEditions,
                                field.value + 1
                              )
                            )
                          }
                          size="small"
                        >
                          <ExpandCircleDownIcon
                            style={{
                              rotate: "270deg",
                              color: theme.colors.amber,
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              />
              {errors.numberOfEditions && (
                <Typography color="error">
                  {errors.numberOfEditions.message}
                </Typography>
              )}
            </Box>
          </CustomCard>

          {/* Step - 4: Boost Your Exposure section */}
          <CustomCard
            title={"Step 4: Boost your exposure"}
            sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <FormSelectField
              name="artMedium"
              disabled={isPublished}
              placeholder="Select art medium"
              label="What medium is this?"
              control={submissionForm.control}
              error={errors.artMedium}
              options={ART_MEDIUM_OPTIONS}
              required
            />

            <FormTextareaField
              name="artDescription"
              disabled={isPublished}
              label="Art Description"
              control={submissionForm.control}
              error={errors.artDescription}
              placeholder="Describe this piece in a short paragraph"
            />

            {/* Subject & Style Checkbox Group */}
            <Box>
              <Typography
                sx={{ color: theme.colors.white, fontSize: "1rem" }}
                gutterBottom
              >
                Subject & Style
              </Typography>

              <FormGroup>
                <Grid container spacing={1}>
                  <Controller
                    name="subjectAndStyle"
                    control={submissionForm.control}
                    render={({ field }) => (
                      <>
                        {SUBJECT_OPTIONS.map((option) => (
                          <Grid item xs={6} lg={3} key={option}>
                            <FormControlLabel
                              key={option}
                              sx={{ color: theme.colors.white }}
                              control={
                                <Checkbox
                                  checked={field.value.includes(option)}
                                  onChange={(e) => {
                                    const updatedValues = e.target.checked
                                      ? [...field.value, option]
                                      : field.value.filter(
                                          (item) => item !== option
                                        );
                                    field.onChange(updatedValues);
                                  }}
                                />
                              }
                              label={option}
                            />
                          </Grid>
                        ))}
                      </>
                    )}
                  />
                </Grid>
              </FormGroup>
              {errors.subjectAndStyle && (
                <Typography color="error">
                  {errors.subjectAndStyle.message}
                </Typography>
              )}
            </Box>

            <FormInputField
              label="Do you have an website?"
              disabled={isPublished}
              control={submissionForm.control}
              name="websiteAddress"
              placeholder={"Website address"}
              error={errors.websiteAddress}
            />

            <FormTagInputField
              name="tags"
              disabled={isPublished}
              hint="Press Enter, after you finish typing."
              control={submissionForm.control}
              label="Add Tags"
              error={errors.tags}
              placeholder="Input tags of you liking"
            />
          </CustomCard>

          {/* Action buttons to save as draft or proceed to next step */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="contained"
              onClick={() => handleSaveDraft({},"",true)}
              disabled={
                loadings.isSavingDraft ||
                isPublished ||
                isUploadingVideo ||
                disableSaveDraftCriteria
              }
              sx={{
                backgroundColor: theme.colors.blue,
                color: "white",
                fontWeight: "bold",
                borderRadius: "50px",
                textTransform: "none",
                padding: "10px 30px",
                "&:hover": { backgroundColor: theme.colors.green },
                "&.Mui-disabled": {
                  backgroundColor: theme.colors.darkGray,
                  color: theme.colors.lightGray,
                },
              }}
            >
              {(loadings.isSavingDraft || isUploadingVideo) && (
                <CachedIcon
                  sx={{ marginRight: "6px", fontSize: "1.2rem" }}
                  className="spin-animation"
                />
              )}
              Save Draft
            </Button>

            <Button
              type="button"
              variant="contained"
              onClick={handleProceedClick}
              disabled={
                isUploadingVideo ||
                Object.keys(errors).length > 0 ||
                loadings.isSavingDraft ||
                isPublished ||
                !submissionForm.watch("videoUploaded") ||
                submissionId === "" ||
                submissionId === "new"
              }
              sx={{
                backgroundColor: theme.colors.green,
                color: "white",
                fontFamily: "basic-sans",
                fontWeight: "bold",
                borderRadius: "50px",
                textTransform: "none",
                padding: "10px 30px",
                "&:hover": {
                  backgroundColor: theme.colors.blue,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.colors.darkGray,
                  color: theme.colors.lightGray,
                },
              }}
            >
              {loadings.isSavingDraft && (
                <CachedIcon
                  sx={{ marginRight: "6px", fontSize: "1.2rem" }}
                  className="spin-animation"
                />
              )}
              Save Draft & Proceed{" "}
              <ArrowForwardIcon
                sx={{ marginLeft: "6px", fontSize: "1.2rem" }}
              />
            </Button>
          </Box>
        </Box>
      )}

      {/* Payment & Submission Section */}
      {pageCurrentStep === "payment" && (
        <Box>
          <UploadArtworkPaymentSection
            onPaymentSuccess={handlePaymentAction}
            oneTimeFee={10}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarOpen={setSnackbarOpen}
            paySubmissionFee={paySubmissionFee}
            handlePublishArtwork={handlePublishArtwork}
            setPageCurrentStep={setPageCurrentStep}
            membership={membership}
            loadings={loadings}
          />
        </Box>
      )}

      {/* Snackbar for error messages */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            backgroundColor:
              snackbarSeverity === "error"
                ? theme.colors.red
                : snackbarSeverity === "success"
                ? theme.colors.green
                : snackbarSeverity === "warning"
                ? theme.colors.amber
                : undefined,
            color: "white",
            width: "100%", // Ensures the Alert spans the width if needed
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UploadArtworkPage;
